import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { highlightRange, rangeFromOffsets, rangeOffsetsWithinProse } from "./selectionTags";
import { renderSampleProse, resolveSourceAnnotation, anchorAnnotation } from "./comparisonProse";
import { useBenchmarkTakeoverStore } from "src/stores/benchmarkTakeoverStore";
import WritingIndicator from "./WritingIndicator";

export default function SampleCard({ sample, precedingSnippet = null }) {
  const text = useBenchmarkTakeoverStore((state) => state.samples[sample].text);
  // Only the pane being read is part of the tab order. During A/B the other
  // pane is a dimmed preview behind it; the compare stage shows both.
  const isActive = useBenchmarkTakeoverStore((state) => {
    const stage = state.stage === "thanks" ? "compare" : state.stage === "loading" ? "A" : state.stage;
    return stage === "compare" || stage === sample;
  });
  const choice = useBenchmarkTakeoverStore((state) => state.choice);
  const ratingSubmitted = useBenchmarkTakeoverStore((state) => state.ratingSubmitted);
  const recordCopyAttempt = useBenchmarkTakeoverStore((state) => state.recordCopyAttempt);

  const annotations = useBenchmarkTakeoverStore((state) => state.annotations);
  // An option that has not streamed yet is still being written (a slow thinker
  // can trail the other side by a minute or more); once the pair is done or
  // failed, an empty pane is a verdict, not a wait.
  const isWriting = useBenchmarkTakeoverStore((state) =>
    !state.samples[sample].text && !state.sampleDone[sample] && !state.generationDone && !state.failure);
  const bodyRef = useRef(null);
  const html = useMemo(() => renderSampleProse(text), [text]);
  const selectingRef = useRef(false);
  const releaseTimerRef = useRef(null);
  const renderedSourceRef = useRef("");

  // Own the prose DOM so we can save the selection before replacing streamed
  // HTML, then restore it and the saved tags against the new text nodes.
  const renderProse = useCallback(() => {
    const body = bodyRef.current;
    if (!body || selectingRef.current) return;
    const prose = body.parentElement;
    const selection = window.getSelection();
    const selectedRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const selected = selectedRange && !selection.isCollapsed &&
      body.contains(selectedRange.startContainer) && body.contains(selectedRange.endContainer)
      ? anchorAnnotation(renderedSourceRef.current, { ...rangeOffsetsWithinProse(selectedRange), text: selectedRange.toString() })
      : null;
    const backwards = selectedRange && selection.anchorNode === selectedRange.endContainer &&
      selection.anchorOffset === selectedRange.endOffset;
    body.innerHTML = html;
    renderedSourceRef.current = text;
    annotations.filter((annotation) => annotation.sample === sample).forEach((annotation) => {
      const range = rangeFromOffsets(prose, resolveSourceAnnotation(text, annotation));
      if (range) highlightRange(range, annotation.custom || annotation.tag);
    });
    const restored = selected && rangeFromOffsets(prose, resolveSourceAnnotation(text, selected));
    if (restored) {
      selection.setBaseAndExtent(
        backwards ? restored.endContainer : restored.startContainer,
        backwards ? restored.endOffset : restored.startOffset,
        backwards ? restored.startContainer : restored.endContainer,
        backwards ? restored.startOffset : restored.endOffset
      );
    }
  }, [html, annotations, sample, text]);

  const latestRenderRef = useRef(renderProse);
  useLayoutEffect(() => {
    latestRenderRef.current = renderProse;
    renderProse();
  }, [renderProse]);

  // Replacing a text node while a mouse drag is in progress destroys the
  // browser's selection anchor, even if we recreate its Range. Hold this
  // pane's paint until release; the store continues receiving every chunk.
  const beginSelection = (event) => {
    if (event.button !== 0 || event.isPrimary === false) return;
    clearTimeout(releaseTimerRef.current);
    selectingRef.current = true;
  };

  useEffect(() => {
    const releaseSelection = () => {
      if (!selectingRef.current) return;
      clearTimeout(releaseTimerRef.current);
      // Let the browser finish pointerup/mouseup before replacing the DOM.
      releaseTimerRef.current = setTimeout(() => {
        selectingRef.current = false;
        latestRenderRef.current();
      }, 0);
    };
    document.addEventListener("pointerup", releaseSelection);
    document.addEventListener("pointercancel", releaseSelection);
    window.addEventListener("blur", releaseSelection);
    return () => {
      clearTimeout(releaseTimerRef.current);
      document.removeEventListener("pointerup", releaseSelection);
      document.removeEventListener("pointercancel", releaseSelection);
      window.removeEventListener("blur", releaseSelection);
    };
  }, []);

  // Copy/cut/drag/context menu stay suppressed on sample text until the rating
  // has been submitted; selection itself stays available for tagging. Copies
  // triggered from outside the pane (Select All from a button) are caught by
  // the takeover's document-level guard.
  const suppressCopy = (event) => {
    if (ratingSubmitted) return;
    event.preventDefault();
    recordCopyAttempt();
  };

  return (
    <article
      className={`sample${precedingSnippet ? " has-context" : ""}`}
      data-card={sample}
      aria-labelledby={`bt-sample-${sample}-title`}
    >
      <span
        className={`save-check${choice === sample ? " is-selected" : ""}`}
        data-card={sample}
        aria-hidden="true"
      ></span>
      <header>
        <div className="sample-title">
          <h2 id={`bt-sample-${sample}-title`}>Option {sample}</h2>
        </div>
      </header>
      {precedingSnippet && (
        <aside
          className="context"
          aria-label="The manuscript text this option continues"
          // Not part of the rated sample: unselectable (so untaggable and
          // uncopyable) via CSS, but left in the accessibility tree so screen
          // reader users hear the context too.
        >
          <div className="context-label">Continues from your manuscript</div>
          <div className="context-body">
            {precedingSnippet.paragraphs.map((paragraph, index) => (
              <p key={index}>
                {index === 0 && precedingSnippet.startsMidParagraph ? "… " : ""}
                {paragraph}
              </p>
            ))}
          </div>
        </aside>
      )}
      <div
        className="prose"
        data-sample={sample}
        tabIndex={isActive ? 0 : -1}
        aria-hidden={isActive ? undefined : true}
        // `inert` also takes the pane's highlight spans (which are focusable so
        // their tag tooltip is reachable) out of the Tab order while inactive.
        inert={isActive ? undefined : ""}
        onPointerDown={beginSelection}
        onCopy={suppressCopy}
        onCut={suppressCopy}
        onDragStart={suppressCopy}
        onContextMenu={suppressCopy}
      >
        <div className="feedback-note">Select text to leave specific feedback</div>
        {isWriting && <WritingIndicator className="sample-writing" />}
        <div className="prose-body" ref={bodyRef} />
      </div>
    </article>
  );
}
